import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import { ReactComponent as HubIcon } from '../../../../../images/icons/hub_icon.svg';
import { ReactComponent as FactoryResetSuccessIcon } from '../../../../../images/icons/factory_reset_success.svg';
import { ReactComponent as FactoryResetFailedIcon } from '../../../../../images/icons/factory_reset_failed.svg';
import NewModal from '../../../../Shared/NewModal';
import { dimGrey } from '../../../../../mainTheme';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { DevicesStore } from '../../../../../store/devices/deviceTypes';
import {
  clearDeviceActionStatus,
  unassignDevice,
} from '../../../../../store/devices/actionCreators';

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

interface FactoryResetDialogProps {
  onClose: () => void;
  deviceId: number;
}

interface StatusViewProps {
  icon: React.ReactElement;
  title: string;
  message: string;
}

const StatusView: React.FC<StatusViewProps> = ({ icon, title, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {icon}
    <Typography sx={{ ...typography.heading, mt: 2 }}>{title}</Typography>
    <Typography sx={{ ...typography.subText, textAlign: 'center' }}>
      {message}
    </Typography>
  </Box>
);

export const FactoryResetDialog: React.FC<FactoryResetDialogProps> = ({
  onClose,
  deviceId,
}) => {
  const { actionStatus } = useSelector<
    RootState,
    { actionStatus: DevicesStore['actionStatus'] }
  >((state) => ({
    actionStatus: state.devices.actionStatus,
  }));

  const dispatch = useDispatch();
  const [step, setStep] = useState<'confirm' | 'success' | 'failed'>('confirm');
  const [verificationText, setVerificationText] = useState('');

  const handleVerification = () => {
    if (verificationText.toLowerCase() === 'reset') {
      dispatch(unassignDevice(deviceId));
    }
  };

  useEffect(() => {
    if (actionStatus.type === 'factory_reset') {
      if (actionStatus.success) {
        setStep('success');
      } else if (actionStatus.success === false) {
        setStep('failed');
      }
    }
  }, [actionStatus]);

  const handleReset = () => {
    dispatch(clearDeviceActionStatus());
    setStep('confirm');
    setVerificationText('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderConfirmView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HubIcon />
      <Typography sx={typography.heading}>Factory reset</Typography>
      <Typography sx={typography.subText}>
        This will delete patient and medication information and unassign the
        Hub. Once complete, the Hub will reboot. This action is not reversible.
      </Typography>
      <Typography sx={{ ...typography.subText, fontWeight: 700, ml: 2 }}>
        Type the word &quot;reset&quot; to confirm.
      </Typography>
      <TextField
        fullWidth
        value={verificationText}
        onChange={(e) => setVerificationText(e.target.value)}
        placeholder="Type 'reset'"
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />
    </Box>
  );

  const renderContent = () => {
    switch (step) {
      case 'confirm':
        return renderConfirmView();
      case 'success':
        return (
          <StatusView
            icon={<FactoryResetSuccessIcon />}
            title="Factory reset complete"
            message="The Hub will reboot shortly."
          />
        );
      case 'failed':
        return (
          <StatusView
            icon={<FactoryResetFailedIcon />}
            title="Factory reset failed"
            message="Please try again at a later time."
          />
        );
    }
  };

  const actionConfig = {
    success: { text: 'OK', onClick: handleClose },
    failed: { text: 'Try again', onClick: handleReset },
    confirm: { text: 'Reset', onClick: handleVerification },
  };

  return (
    <NewModal
      showCloseX
      title="Factory reset"
      onClose={handleClose}
      maxWidth="xs"
      actions={{
        primaryAction: {
          ...actionConfig[step],
          disabled:
            step === 'confirm' && verificationText.toLowerCase() !== 'reset',
        },
        secondaryAction:
          step === 'confirm'
            ? {
                text: 'Back',
                onClick: handleClose,
              }
            : undefined,
      }}
    >
      {renderContent()}
    </NewModal>
  );
};
